<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>添加书签</h2>
        <button class="close-btn" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-form">
        <div class="form-group">
          <label for="url">
            网址 *
            <span v-if="isFetchingMeta" class="fetch-hint">正在获取网站信息...</span>
          </label>
          <input
            id="url"
            v-model="form.url"
            type="url"
            placeholder="https://example.com"
            required
            @blur="fetchMetadata"
          />
        </div>

        <div class="form-group">
          <label for="name">名称 *</label>
          <input
            id="name"
            v-model="form.name"
            type="text"
            placeholder="网站名称"
            required
          />
        </div>

        <div class="form-group">
          <label for="description">描述</label>
          <textarea
            id="description"
            v-model="form.description"
            placeholder="简短描述这个网站..."
            rows="2"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="category">分类 *</label>
          <select id="category" v-model="form.categoryId" required>
            <option value="">选择分类</option>
            <optgroup v-for="group in navStore.groupedCategories" :key="group.id" :label="group.name">
              <option v-for="cat in group.items" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </optgroup>
          </select>
        </div>

        <div class="form-group">
          <label for="tags">标签</label>
          <input
            id="tags"
            v-model="tagsInput"
            type="text"
            placeholder="用逗号分隔多个标签，如：前端, Vue, 学习"
          />
          <div v-if="form.tags.length" class="tags-preview">
            <span v-for="(tag, index) in form.tags" :key="index" class="tag">
              {{ tag }}
              <button type="button" @click="removeTag(index)">×</button>
            </span>
          </div>
        </div>

        <div class="form-group">
          <label for="icon">图标URL</label>
          <div class="icon-input">
            <input
              id="icon"
              v-model="form.icon"
              type="url"
              placeholder="https://example.com/favicon.ico"
            />
            <div v-if="form.icon" class="icon-preview">
              <img :src="form.icon" @error="handleIconError" />
            </div>
          </div>
        </div>

        <div v-if="errorMsg" class="form-error">{{ errorMsg }}</div>

        <div class="form-actions">
          <button type="button" class="btn-secondary" @click="$emit('close')">
            取消
          </button>
          <button type="submit" class="btn-primary" :disabled="isSubmitting">
            {{ isSubmitting ? '添加中...' : '添加书签' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useNavStore } from '@/stores/nav'
import contentParser from '@/services/contentParser'

const emit = defineEmits(['close'])
const navStore = useNavStore()

const form = reactive({
  url: '',
  name: '',
  description: '',
  categoryId: '',
  icon: '',
  tags: []
})

const tagsInput = ref('')
const isSubmitting = ref(false)
const isFetchingMeta = ref(false)
const errorMsg = ref('')

watch(tagsInput, (value) => {
  if (value.includes(',')) {
    const newTags = value.split(',').map(t => t.trim()).filter(t => t && !form.tags.includes(t))
    form.tags.push(...newTags)
    tagsInput.value = ''
  }
})

function removeTag(index) {
  form.tags.splice(index, 1)
}

async function fetchMetadata() {
  if (!form.url || isFetchingMeta.value) return

  let url
  try {
    url = new URL(form.url)
  } catch (e) {
    return // 无效URL，忽略
  }

  isFetchingMeta.value = true
  try {
    // 解析网站标题、描述、图标等基本信息，自动填充空字段
    const info = await contentParser.parseUrl(form.url)
    if (!form.name) {
      form.name = info.title || url.hostname.replace('www.', '')
    }
    if (!form.description && info.description) {
      form.description = info.description
    }
    if (!form.icon) {
      form.icon = info.favicon || contentParser.getFaviconUrl(form.url)
    }
  } catch (e) {
    if (!form.name) {
      form.name = url.hostname.replace('www.', '')
    }
    if (!form.icon) {
      form.icon = contentParser.getFaviconUrl(form.url)
    }
  } finally {
    isFetchingMeta.value = false
  }
}

function handleIconError() {
  // 图标加载失败时回退到 Google favicon 服务，再失败才清空
  let fallback = ''
  try {
    fallback = contentParser.getFaviconUrl(form.url)
  } catch (e) {
    // 无效URL，忽略
  }
  form.icon = form.icon === fallback ? '' : fallback
}

async function handleSubmit() {
  errorMsg.value = ''

  if (!form.url || !form.name || !form.categoryId) {
    errorMsg.value = '请填写网址、名称并选择分类'
    return
  }

  isSubmitting.value = true

  try {
    if (tagsInput.value.trim()) {
      form.tags.push(...tagsInput.value.split(',').map(t => t.trim()).filter(t => t))
    }

    await navStore.addBookmark({
      url: form.url,
      name: form.name,
      description: form.description,
      categoryId: form.categoryId,
      icon: form.icon,
      tags: [...form.tags]
    })

    emit('close')
  } catch (error) {
    console.error('添加书签失败:', error)
    errorMsg.value = '添加书签失败，请重试'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
  
  h2 {
    font-size: var(--text-xl);
    font-weight: 600;
  }
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  transition: all var(--transition-fast);
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  
  label {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--text-secondary);
  }
  
  input,
  textarea,
  select {
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--text-sm);
    transition: border-color var(--transition-fast);
    
    &::placeholder {
      color: var(--text-muted);
    }
    
    &:focus {
      border-color: var(--primary);
      outline: none;
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 60px;
  }
  
  select {
    cursor: pointer;
  }
}

.icon-input {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  
  input {
    flex: 1;
  }
}

.icon-preview {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border-radius: var(--radius-md);
  
  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
}

.tags-preview {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-xs);
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: var(--primary);
  color: white;
  font-size: var(--text-xs);
  border-radius: var(--radius-full);
  
  button {
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    opacity: 0.7;
    
    &:hover {
      opacity: 1;
    }
  }
}

.fetch-hint {
  font-size: var(--text-xs);
  font-weight: 400;
  color: var(--primary);
}

.form-error {
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: var(--radius-md);
  color: #ef4444;
  font-size: var(--text-sm);
}

.form-actions {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  margin-top: var(--spacing-md);
}

.btn-secondary,
.btn-primary {
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 500;
  transition: all var(--transition-fast);
}

.btn-secondary {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  
  &:hover {
    background: var(--border-color);
  }
}

.btn-primary {
  background: var(--primary);
  color: white;
  
  &:hover:not(:disabled) {
    background: var(--primary-dark);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
